/*
 * myfence — Hero "Roll die Hecke"
 * Interaktiver Canvas-Hintergrund für den Startseiten-Hero.
 *
 * Prozedurale 2D-Szene: grauer Himmel + triste Stadt hinter einem Staketenzaun;
 * eine Folienrolle bezieht den Zaun bei Zeiger-/Fingerbewegung mit grüner
 * Sichtschutz-Folie. cm-Anzeige zählt die "bezogene" Breite (max 300 cm) mit.
 *
 * Steuerung ausschließlich per Zeiger/Finger (kein Auto-Play, kein Idle-Drift).
 * Zeichenlogik portiert aus der Design-Referenz (Vanilla-JS), sauber gekapselt:
 * mount() startet den requestAnimationFrame-Loop, destroy() räumt via
 * cancelAnimationFrame + Listener-/ResizeObserver-Entfernung wieder auf.
 * prefers-reduced-motion: fester Fortschritt (~0.55), kein Dauer-Loop.
 */
(function () {
  'use strict';

  var FONT = 'Manrope, "DM Sans", system-ui, sans-serif';
  var GREENS = ['#3a7440', '#4f9350', '#63ad5b', '#7dc063', '#2c5a34', '#20492a'];
  var DARK_GREENS = ['#1f4626', '#173a1e', '#275633', '#123019'];
  var ACCENTS = ['#7d3b52', '#8a3f4c', '#a15236'];
  var MAXCM = 300;

  function rnd(rng, a, b) { return a + rng() * (b - a); }

  /* Deterministischer PRNG (mulberry32) — stabile Szene je Größe, kein Date/Math.random-Flackern. */
  function makeRng(seed) {
    var s = seed >>> 0;
    return function () {
      s |= 0; s = (s + 0x6D2B79F5) | 0;
      var t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function MfHeroRoll(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.W = 1; this.H = 1; this.dpr = 1;
    this.P = { px: 0.16, tx: 0.16 };
    this._fence = null; this._folie = null; this._city = null;
    this._trist = null; this._gruen = null;
    this._raf = 0; this._running = false; this._mounted = false;
    this._mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    // Animation nur ab Tablet-Hochkant (>=768px) — auf dem Handy zeigt CSS den grünen Fallback.
    this._mqDesktop = window.matchMedia ? window.matchMedia('(min-width: 768px)') : null;
    this._reduced = !!(this._mq && this._mq.matches);
    this._onResize = this._onResize.bind(this);
    this._setTarget = this._setTarget.bind(this);
    this._frame = this._frame.bind(this);
    this._onMqChange = this._onMqChange.bind(this);
  }

  // Läuft die Animation auf dieser Breite? (>=768px = Desktop/Tablet)
  MfHeroRoll.prototype._allowed = function () { return this._mqDesktop ? this._mqDesktop.matches : true; };

  MfHeroRoll.prototype.mount = function () {
    if (this._mounted) return;
    this._mounted = true;

    this._ro = new ResizeObserver(this._onResize);
    this._ro.observe(this.canvas);

    this.canvas.addEventListener('pointermove', this._setTarget);
    this.canvas.addEventListener('touchmove', this._setTarget, { passive: true });
    if (this._mq && this._mq.addEventListener) this._mq.addEventListener('change', this._onMqChange);
    if (this._mqDesktop && this._mqDesktop.addEventListener) this._mqDesktop.addEventListener('change', this._onMqChange);

    this._apply();
  };

  MfHeroRoll.prototype._stopLoop = function () {
    this._running = false;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
  };

  // Zentrale Zustandslogik: entscheidet je nach Viewport/Reduced-Motion, ob & wie gezeichnet wird.
  // Idempotent — stoppt zuerst einen laufenden Loop.
  MfHeroRoll.prototype._apply = function () {
    if (!this._mounted) return;
    this._stopLoop();
    if (!this._allowed()) return;             // Handy (<768px): nichts (Canvas per CSS ausgeblendet)
    this._measure();                          // frische Größe + prozedurale Daten
    if (this._reduced) {
      this.P.px = this.P.tx = 0.55;           // fester Fortschritt, KEIN Dauer-Loop
      this._renderOnce();
      return;
    }
    this._running = true;
    this._raf = requestAnimationFrame(this._frame);
  };

  MfHeroRoll.prototype.destroy = function () {
    this._mounted = false;
    this._stopLoop();
    if (this._ro) { this._ro.disconnect(); this._ro = null; }
    this.canvas.removeEventListener('pointermove', this._setTarget);
    this.canvas.removeEventListener('touchmove', this._setTarget);
    if (this._mq && this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMqChange);
    if (this._mqDesktop && this._mqDesktop.removeEventListener) this._mqDesktop.removeEventListener('change', this._onMqChange);
  };

  // Reagiert auf prefers-reduced-motion UND auf das Überschreiten der Mobil-Grenze (768px).
  MfHeroRoll.prototype._onMqChange = function () {
    this._reduced = !!(this._mq && this._mq.matches);
    this._apply();
  };

  // ResizeObserver-Callback: Größe neu vermessen; Start/Stopp bei Über-/Unterschreiten der Grenze.
  MfHeroRoll.prototype._onResize = function () {
    if (!this._mounted) return;
    if (!this._allowed()) { this._apply(); return; }   // auf Handygröße geschrumpft -> stoppen
    if (!this._running && !this._reduced) { this._apply(); return; } // zurück auf Desktop -> starten
    this._measure();                                   // laufend: nur neu dimensionieren
    if (this._reduced) this._renderOnce();
  };

  // Reines Vermessen (DPR-korrekt) + prozedurale Daten zum Neuaufbau markieren.
  MfHeroRoll.prototype._measure = function () {
    var r = this.canvas.getBoundingClientRect();
    this.W = Math.max(1, r.width);
    this.H = Math.max(1, r.height);
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(this.W * this.dpr);
    this.canvas.height = Math.round(this.H * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this._fence = null; this._folie = null; this._city = null;
    this._trist = null; this._gruen = null;   // Layer-Cache in neuer Größe neu aufbauen
  };

  MfHeroRoll.prototype._setTarget = function (e) {
    if (this._reduced || !this._allowed()) return;
    var r = this.canvas.getBoundingClientRect();
    var cx = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
    this.P.tx = Math.min(0.985, Math.max(0.02, (cx - r.left) / r.width));
  };

  MfHeroRoll.prototype._band = function () { return { ft: this.H * 0.44, fb: this.H * 0.9 }; };

  /* ---- prozedurale Daten ------------------------------------------------- */

  MfHeroRoll.prototype._buildFolie = function () {
    var rng = makeRng(Math.round(this.W) * 73856093 ^ Math.round(this.H) * 19349663);
    this._folie = [];
    var area = this.W * this.H * 0.46;
    var back = Math.round(area / 2200), front = Math.round(area / 3800);
    var mk = function (isBack) {
      var red = !isBack && rng() < 0.13;
      return {
        x: rng(), y: rng(), s: isBack ? rnd(rng, 24, 46) : rnd(rng, 13, 30),
        rot: rnd(rng, -0.9, 0.9) + (rng() < 0.5 ? 0 : 3.14), ph: rnd(rng, 0, 6.28), back: isBack,
        c: isBack ? DARK_GREENS[Math.floor(rng() * DARK_GREENS.length)]
                  : (red ? ACCENTS[Math.floor(rng() * ACCENTS.length)]
                         : GREENS[Math.floor(rng() * GREENS.length)])
      };
    };
    for (var i = 0; i < back; i++) this._folie.push(mk(true));
    for (var j = 0; j < front; j++) this._folie.push(mk(false));
  };

  MfHeroRoll.prototype._buildFence = function () { this._fence = Math.max(14, Math.round(this.W / 46)); };

  MfHeroRoll.prototype._buildCity = function () {
    var rng = makeRng(Math.round(this.W) * 83492791 ^ Math.round(this.H) * 2971215073);
    this._city = [];
    var n = Math.max(11, Math.round(this.W / 44));
    for (var i = 0; i < n; i++) {
      this._city.push({ x: rng(), w: rnd(rng, 0.028, 0.075), h: rnd(rng, 0.3, 0.95), far: rng() < 0.45, seed: rng() });
    }
    this._city.sort(function (a, b) { return (a.far === b.far) ? 0 : (a.far ? -1 : 1); });
  };

  /* ---- Zeichen-Primitive -------------------------------------------------- */

  MfHeroRoll.prototype._leaf = function (x, y, s, rot, c, a) {
    var ctx = this.ctx;
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.globalAlpha = a; ctx.fillStyle = c;
    ctx.beginPath();
    ctx.moveTo(0, -s);
    ctx.bezierCurveTo(s * 0.6, -s * 0.55, s * 0.55, s * 0.55, 0, s);
    ctx.bezierCurveTo(-s * 0.55, s * 0.55, -s * 0.6, -s * 0.55, 0, -s);
    ctx.fill();
    ctx.globalAlpha = a * 0.5; ctx.strokeStyle = 'rgba(0,0,0,.25)'; ctx.lineWidth = Math.max(0.6, s * 0.03);
    ctx.beginPath(); ctx.moveTo(0, -s * 0.85); ctx.lineTo(0, s * 0.85); ctx.stroke();
    ctx.restore();
  };

  MfHeroRoll.prototype._grommet = function (x, y, r) {
    var ctx = this.ctx;
    ctx.save();
    ctx.beginPath(); ctx.arc(x, y, r, 0, 6.29); ctx.fillStyle = 'rgba(245,248,242,.95)'; ctx.fill();
    var g = ctx.createLinearGradient(x - r, y - r, x + r, y + r);
    g.addColorStop(0, '#e4e7e0'); g.addColorStop(0.5, 'rgba(139,145,135,.5)'); g.addColorStop(1, '#b9beb6');
    ctx.beginPath(); ctx.arc(x, y, r * 0.72, 0, 6.29); ctx.lineWidth = r * 0.26; ctx.strokeStyle = g; ctx.stroke();
    ctx.beginPath(); ctx.arc(x, y, r * 0.46, 0, 6.29); ctx.fillStyle = 'rgba(12,26,14,.9)'; ctx.fill();
    ctx.restore();
  };

  /* ---- Ebenen (hinten -> vorne) ------------------------------------------ */

  MfHeroRoll.prototype._drawSky = function (coverX) {
    var ctx = this.ctx, W = this.W, ft = this._band().ft;
    var g = ctx.createLinearGradient(0, 0, 0, ft);
    g.addColorStop(0, '#969da6'); g.addColorStop(1, '#c6cbd0');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, ft + 2);
    if (coverX > 2) {
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, coverX, ft); ctx.clip();
      var sun = ctx.createRadialGradient(coverX * 0.45, ft * 0.15, 0, coverX * 0.45, ft * 0.15, ft * 1.1);
      sun.addColorStop(0, 'rgba(255,226,155,.42)'); sun.addColorStop(0.5, 'rgba(255,236,190,.16)'); sun.addColorStop(1, 'rgba(255,240,210,0)');
      ctx.fillStyle = sun; ctx.fillRect(0, 0, coverX, ft);
      ctx.restore();
    }
  };

  MfHeroRoll.prototype._drawBackdrop = function () {
    var ctx = this.ctx, W = this.W, b = this._band();
    var bd = ctx.createLinearGradient(0, b.ft, 0, b.fb);
    bd.addColorStop(0, '#c6cbd0'); bd.addColorStop(1, '#b0b5ba');
    ctx.fillStyle = bd; ctx.fillRect(0, b.ft, W, b.fb - b.ft);
  };

  MfHeroRoll.prototype._drawCity = function (coverX) {
    var ctx = this.ctx, W = this.W, b = this._band(), fb = b.fb;
    for (var i = 0; i < this._city.length; i++) {
      var bl = this._city[i];
      var bx = bl.x * W, bw = bl.w * W, bh = bl.h * fb * 0.9, by = fb - bh, lit = bx < coverX;
      ctx.fillStyle = bl.far ? (lit ? '#949aa0' : '#8a8f96') : (lit ? '#6e727a' : '#5f636b');
      ctx.fillRect(bx - bw / 2, by, bw, bh);
      ctx.fillStyle = 'rgba(255,255,255,.05)'; ctx.fillRect(bx - bw / 2, by, bw, Math.max(2, bh * 0.02));
      var cols = Math.max(1, Math.round(bw / 10)), rowsN = Math.max(2, Math.round(bh / 13));
      for (var r = 0; r < rowsN; r++) {
        for (var cc = 0; cc < cols; cc++) {
          var wx = bx - bw / 2 + bw * (cc + 0.5) / cols, wy = by + bh * (r + 0.6) / rowsN;
          var on = Math.sin(r * 3.7 + cc * 1.9 + bl.seed * 30) > 0.72;
          ctx.fillStyle = on ? (lit ? 'rgba(255,224,150,.5)' : 'rgba(205,212,222,.32)') : 'rgba(28,32,38,.4)';
          ctx.fillRect(wx - 1.5, wy - 2.3, 3, 4.6);
        }
      }
    }
    // durchgehender Dunst-Schleier über die ganze Höhe (kein Bruch an der Zaunlinie)
    var fog = ctx.createLinearGradient(0, 0, 0, fb);
    fog.addColorStop(0, 'rgba(201,206,211,.42)'); fog.addColorStop(1, 'rgba(201,206,211,.14)');
    ctx.fillStyle = fog; ctx.fillRect(0, 0, W, fb);
  };

  MfHeroRoll.prototype._drawGround = function (coverX) {
    var ctx = this.ctx, W = this.W, H = this.H, fb = this._band().fb;
    var gd = ctx.createLinearGradient(0, fb, 0, H);
    gd.addColorStop(0, '#6b6b58'); gd.addColorStop(1, '#4b4b3f');
    ctx.fillStyle = gd; ctx.fillRect(0, fb - 2, W, H - fb + 2);
    if (coverX > 2) {
      ctx.save(); ctx.beginPath(); ctx.rect(0, fb - 2, coverX, H - fb + 2); ctx.clip();
      var gl = ctx.createLinearGradient(0, fb, 0, H);
      gl.addColorStop(0, '#3f7a45'); gl.addColorStop(1, '#2a5533');
      ctx.fillStyle = gl; ctx.fillRect(0, fb - 2, coverX, H - fb + 2); ctx.restore();
    }
  };

  MfHeroRoll.prototype._drawFenceBare = function (fromX) {
    var ctx = this.ctx, W = this.W, b = this._band(), ft = b.ft, fb = b.fb, h = fb - ft;
    var n = this._fence, slot = W / n, sw = Math.max(4, slot * 0.26);
    for (var i = 0; i < n; i++) {
      var cx = (i + 0.5) * slot; if (cx + sw < fromX) continue;
      var g = ctx.createLinearGradient(cx - sw / 2, 0, cx + sw / 2, 0);
      g.addColorStop(0, '#6f6249'); g.addColorStop(0.32, '#ab9a76'); g.addColorStop(0.5, '#bcac84'); g.addColorStop(0.7, '#9c8b66'); g.addColorStop(1, '#635840');
      ctx.fillStyle = g;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(cx - sw / 2, ft, sw, h, sw * 0.5); else ctx.rect(cx - sw / 2, ft, sw, h);
      ctx.fill();
      ctx.beginPath(); ctx.moveTo(cx - sw / 2, ft + sw * 0.25); ctx.lineTo(cx, ft - sw * 0.6); ctx.lineTo(cx + sw / 2, ft + sw * 0.25); ctx.closePath();
      ctx.fillStyle = '#bcac84'; ctx.fill();
    }
    ctx.fillStyle = 'rgba(150,134,96,.92)';
    ctx.fillRect(fromX, ft + h * 0.16, W - fromX, Math.max(3, h * 0.035));
    ctx.fillRect(fromX, ft + h * 0.72, W - fromX, Math.max(3, h * 0.035));
  };

  MfHeroRoll.prototype._drawFolie = function (toX, t) {
    var ctx = this.ctx, W = this.W, b = this._band(), ft = b.ft, fb = b.fb, h = fb - ft, f, fx, i;
    ctx.save(); ctx.beginPath(); ctx.rect(0, ft - h * 0.08, toX, h * 1.16); ctx.clip();
    var g = ctx.createLinearGradient(0, ft, 0, fb);
    g.addColorStop(0, '#2c5a34'); g.addColorStop(0.5, '#1d4725'); g.addColorStop(1, '#12301b');
    ctx.fillStyle = g; ctx.fillRect(0, ft - h * 0.1, toX, h * 1.2);
    var sw = Math.sin(t * 0.0007) * 2;
    for (i = 0; i < this._folie.length; i++) {
      f = this._folie[i]; if (!f.back) continue; fx = f.x * W; if (fx > toX + 40) continue;
      this._leaf(fx + sw * 0.6, ft + f.y * h, f.s, f.rot, f.c, 0.72);
    }
    ctx.shadowColor = 'rgba(0,18,4,.32)'; ctx.shadowBlur = 6; ctx.shadowOffsetY = 3;
    for (i = 0; i < this._folie.length; i++) {
      f = this._folie[i]; if (f.back) continue; fx = f.x * W; if (fx > toX + 40) continue;
      this._leaf(fx + sw, ft + f.y * h, f.s, f.rot + Math.sin(t * 0.0005 + f.ph) * 0.04, f.c, 0.96);
    }
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    var gr = Math.max(3, h * 0.026), pad = W * 0.045, gy1 = ft + h * 0.016, gy2 = fb - h * 0.016;
    var cols = Math.max(2, Math.round((W - 2 * pad) / (W * 0.16)));
    for (i = 0; i <= cols; i++) {
      var gx = pad + (i / cols) * (W - 2 * pad);
      if (gx < toX - gr * 1.5) { this._grommet(gx, gy1, gr); this._grommet(gx, gy2, gr); }
    }
    var shn = ctx.createLinearGradient(0, ft, toX * 0.9, fb);
    shn.addColorStop(0, 'rgba(255,255,255,.06)'); shn.addColorStop(0.5, 'rgba(255,255,255,0)'); shn.addColorStop(1, 'rgba(0,0,0,.05)');
    ctx.fillStyle = shn; ctx.fillRect(0, ft - h * 0.1, toX, h * 1.2);
    ctx.restore();
  };

  MfHeroRoll.prototype._drawRoll = function (x, t) {
    var ctx = this.ctx, W = this.W, H = this.H, b = this._band(), ft = b.ft, fb = b.fb;
    var top = ft - Math.max(10, (fb - ft) * 0.1), bot = fb + Math.max(6, (fb - ft) * 0.05), hh = bot - top;
    var rw = Math.max(13, W * 0.018);

    // Kontaktschatten auf dem Zaun
    var sg = ctx.createLinearGradient(x, 0, x + rw * 4, 0);
    sg.addColorStop(0, 'rgba(0,0,0,.3)'); sg.addColorStop(0.5, 'rgba(0,0,0,.1)'); sg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = sg; ctx.fillRect(x, top, rw * 4, hh);

    var cap = rw * 0.32;
    // untere Kappe
    ctx.fillStyle = '#cbcec6';
    ctx.beginPath(); ctx.ellipse(x, bot, rw, cap, 0, 0, 6.29); ctx.fill();
    // Zylinderkörper (gerade Seiten, bündig)
    var rg = ctx.createLinearGradient(x - rw, 0, x + rw, 0);
    rg.addColorStop(0, '#a8aca4'); rg.addColorStop(0.24, '#f6f8f4'); rg.addColorStop(0.46, '#fdfefc'); rg.addColorStop(0.7, '#d3d6ce'); rg.addColorStop(1, '#93978f');
    ctx.fillStyle = rg; ctx.fillRect(x - rw, top, rw * 2, hh);

    // Rotations-Andeutung: feine Wickel-Streifen, laufen beim Abrollen seitlich mit
    ctx.save();
    ctx.beginPath(); ctx.rect(x - rw, top, rw * 2, hh); ctx.clip();
    var phase = (this.P.px * W * 0.7) % (rw * 1.4);
    ctx.strokeStyle = 'rgba(120,126,118,.26)'; ctx.lineWidth = 1.2;
    for (var sx = -rw * 1.4 + phase; sx < rw; sx += rw * 0.7) {
      ctx.beginPath(); ctx.moveTo(x + sx, top); ctx.lineTo(x + sx, bot); ctx.stroke();
    }
    ctx.restore();
    ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fillRect(x - rw * 0.42, top, rw * 0.14, hh);

    // obere Kappe — sauber, zentriert, identisch geformt
    var capGrad = ctx.createRadialGradient(x, top, 0, x, top, rw);
    capGrad.addColorStop(0, '#fbfcfa'); capGrad.addColorStop(0.7, '#e7e9e3'); capGrad.addColorStop(1, '#cfd2cb');
    ctx.fillStyle = capGrad;
    ctx.beginPath(); ctx.ellipse(x, top, rw, cap, 0, 0, 6.29); ctx.fill();
    ctx.strokeStyle = 'rgba(120,126,118,.5)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(x, top, rw * 0.62, cap * 0.62, 0, 0, 6.29); ctx.stroke();
    ctx.strokeStyle = 'rgba(120,126,118,.35)';
    ctx.beginPath(); ctx.ellipse(x, top, rw * 0.28, cap * 0.28, 0, 0, 6.29); ctx.stroke();
    // dezente Randlinien der Kappen
    ctx.strokeStyle = 'rgba(0,0,0,.12)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(x, top, rw, cap, 0, 0, 6.29); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(x, bot, rw, cap, 0, Math.PI, false); ctx.stroke();

    // cm-Chip
    var cm = Math.round(this.P.px * MAXCM), label = cm + ' cm';
    var numF = '700 ' + Math.max(14, H * 0.05).toFixed(0) + 'px ' + FONT;
    ctx.font = numF; var tw = ctx.measureText(label).width;
    var capF = '600 ' + Math.max(8, H * 0.018).toFixed(0) + 'px ' + FONT;
    ctx.font = capF; var cw = ctx.measureText('BREITE').width;
    var chW = Math.max(tw, cw) + H * 0.06, chH = H * 0.108;
    var chX = Math.min(x + rw + 11, W - chW - 6);   // rechts neben der Rolle, bleibt im Bild
    var chY = H - chH - 6;                           // unterhalb (Bodenbereich), frei von H1/Teaser
    ctx.fillStyle = 'rgba(16,28,18,.74)';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(chX, chY, chW, chH, chH * 0.28); else ctx.rect(chX, chY, chW, chH);
    ctx.fill();
    ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.stroke();
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.font = capF; ctx.fillStyle = '#f5b877'; ctx.fillText('BREITE', chX + H * 0.03, chY + chH * 0.36);
    ctx.font = numF; ctx.fillStyle = '#fff'; ctx.fillText(label, chX + H * 0.03, chY + chH * 0.82);
  };

  MfHeroRoll.prototype._drawTape = function (forceDone) {
    var ctx = this.ctx, W = this.W, b = this._band(), ft = b.ft, fb = b.fb;
    var y = Math.max(3, ft - Math.max(8, (fb - ft) * 0.08) - 8);
    for (var c = 0; c <= MAXCM; c += 20) {
      var x = (c / MAXCM) * W, major = c % 100 === 0;
      var done = (forceDone === undefined) ? ((c / MAXCM) <= this.P.px) : forceDone;
      ctx.strokeStyle = done ? 'rgba(255,255,255,.75)' : 'rgba(80,88,96,.5)'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y - (major ? 10 : 5)); ctx.stroke();
      if (major && c > 0) {
        ctx.fillStyle = done ? 'rgba(255,255,255,.85)' : 'rgba(80,88,96,.7)';
        ctx.font = '600 10px ' + FONT; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
        ctx.fillText((c / 100) + ' m', x, y - 12);
      }
    }
  };

  /* ---- Layer-Caching (Performance) --------------------------------------
     Die teuren, größenabhängigen Ebenen (Stadt-Fenster, Blatt-Silhouetten mit
     Schatten, Ösen, Zaun) werden EINMAL pro Größe in zwei Offscreen-Canvas
     vorgerendert: "trist" (unbezogen) und "grün" (bezogen). Pro Frame wird nur
     noch grün links / trist rechts der Rolle per drawImage zusammengesetzt und
     die bewegte Rolle live gezeichnet — statt jede Ebene neu zu berechnen. */

  MfHeroRoll.prototype._newLayer = function () {
    var c = document.createElement('canvas');
    c.width = Math.round(this.W * this.dpr);
    c.height = Math.round(this.H * this.dpr);
    var x = c.getContext('2d');
    x.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    return { canvas: c, ctx: x };
  };

  // Zeichnet die komplette Szene (ohne Rolle) in this.ctx — lit=false: trist, lit=true: bezogen/grün.
  MfHeroRoll.prototype._paintScene = function (lit) {
    var cover = lit ? this.W : 0;
    this._drawSky(cover);
    this._drawBackdrop();
    this._drawCity(cover);
    this._drawGround(cover);
    if (lit) this._drawFolie(this.W, 0); else this._drawFenceBare(0);
    this._drawTape(lit);
  };

  MfHeroRoll.prototype._buildLayers = function () {
    if (this._folie === null) this._buildFolie();
    if (this._fence === null) this._buildFence();
    if (this._city === null) this._buildCity();
    var real = this.ctx;
    var trist = this._newLayer();
    this.ctx = trist.ctx; this._paintScene(false);
    var gruen = this._newLayer();
    this.ctx = gruen.ctx; this._paintScene(true);
    this.ctx = real;
    this._trist = trist.canvas;
    this._gruen = gruen.canvas;
  };

  /* ---- Loop -------------------------------------------------------------- */

  MfHeroRoll.prototype._draw = function (t) {
    if (!this._trist || !this._gruen) this._buildLayers();
    var ctx = this.ctx, W = this.W, H = this.H, rx = this.P.px * W;
    ctx.clearRect(0, 0, W, H);
    if (rx > 0) {                                   // bezogen (grün) links der Rolle
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, rx, H); ctx.clip();
      ctx.drawImage(this._gruen, 0, 0, W, H); ctx.restore();
    }
    if (rx < W) {                                   // trist rechts der Rolle
      ctx.save(); ctx.beginPath(); ctx.rect(rx, 0, W - rx, H); ctx.clip();
      ctx.drawImage(this._trist, 0, 0, W, H); ctx.restore();
    }
    this._drawRoll(rx, t);                          // bewegte Rolle + cm-Chip live
  };

  MfHeroRoll.prototype._renderOnce = function () { this._draw(0); };

  MfHeroRoll.prototype._frame = function (now) {
    if (!this._running) return;
    if (!this._allowed()) { this._stopLoop(); return; }   // Robustheit: stoppt auch ohne MQ-Event
    this.P.px += (this.P.tx - this.P.px) * 0.35;   // schnelle, leicht geglättete Nachführung
    this._draw(now);
    this._raf = requestAnimationFrame(this._frame);
  };

  /* ---- Controller + Shopify-Section-Lifecycle ---------------------------- */

  function initIn(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var list = scope.querySelectorAll('canvas.mf-hero-canvas');
    for (var i = 0; i < list.length; i++) {
      var cv = list[i];
      if (cv.mfHeroRoll) continue;
      var inst = new MfHeroRoll(cv);
      cv.mfHeroRoll = inst;
      inst.mount();
    }
  }

  function destroyIn(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var list = scope.querySelectorAll('canvas.mf-hero-canvas');
    for (var i = 0; i < list.length; i++) {
      var cv = list[i];
      if (cv.mfHeroRoll) { cv.mfHeroRoll.destroy(); cv.mfHeroRoll = null; }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initIn(document); });
  } else {
    initIn(document);
  }

  // Theme-Editor: Section-Neuladen sauber ab-/aufbauen (verhindert doppelte rAF-Loops)
  document.addEventListener('shopify:section:load', function (e) { initIn(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });

  window.MfHeroRoll = MfHeroRoll;
})();
